import { useSelector } from 'react-redux';
import { createSelector } from '@reduxjs/toolkit';
import classNames from 'classnames';
import I18n from 'i18n-js';
import { bool, func, node, number, oneOfType, string } from 'prop-types';

import ActivityIndicator from '~/components/ActivityIndicator';
import Image from '~/components/Image';
import LootBoyButton from '~/components/LootBoyButton';
import { DialogWithTransition } from '~/components/PopupsWithTransition';

import { CURRENCY_I18N_IDS } from '~/constants/payments';
import { lootsSelector } from '~/redux/selectors';

import { usePriceLabel } from '../shared';

import styleLootpacks from '../LootPackViews.module.scss';
import style from './ExchangeCurrencyDialog.module.scss';

export const lootSelector = createSelector(
  lootsSelector,
  (_, lootId) => lootId,
  (loots, lootId) => loots[lootId] || {},
);

const OptionWrapper = ({ children, light }) => {
  const color = light ? style.light : style.dark;
  return <div className={classNames(style.option, color)}>{children}</div>;
};

OptionWrapper.propTypes = {
  children: node,
  light: bool,
};

const ExchangeCurrencyDialog = ({
  currency,
  price,
  lootId,
  onClose,
  show,
  bundleSelected,
  balance,
  onPurchase,
  processing,
}) => {
  const {
    name = '',
    headline,
    subHeadline,
    bundleImage,
  } = useSelector((state) => lootSelector(state, lootId));
  const priceLabel = usePriceLabel({
    currency,
    price,
  });
  const currencyLabel = CURRENCY_I18N_IDS[currency] || '';
  const currencyTranslation = currencyLabel && I18n.t(currencyLabel);

  return (
    <DialogWithTransition
      show={show}
      onClose={onClose}
      containerClassName={style.modalContainer}
      className={style.container}
      dismissButtonClassName={style.dismissButton}
    >
      <div className={style.headers}>
        <h3 className={style.headline}>
          {!headline && !subHeadline ? name : headline}
        </h3>
        {subHeadline && <h5 className={style.subHeadline}>{subHeadline}</h5>}
      </div>
      <OptionWrapper light>
        <div>{`${bundleSelected.replace(/\D/g, '')}x`}</div>
        <Image
          alt="Bundle image"
          path={bundleImage}
          className={style.image}
        />
      </OptionWrapper>
      <div className={style.balanceWrapper}>
        <OptionWrapper>
          <div
            className={classNames(styleLootpacks.price, {
              [(currency || '').toLowerCase()]: 1,
            })}
          >
            {priceLabel}
          </div>
        </OptionWrapper>
        {Boolean(balance) && (
          <div className={classNames(style.balance, { processing })}>
            {I18n.t('ticket_redemption_new_balance')}
            <b>
              {` ${balance}`}
              &nbsp;
              {`${currencyTranslation}`}
            </b>
          </div>
        )}
      </div>
      {processing ? (
        <div>
          <ActivityIndicator />
        </div>
      ) : (
        <LootBoyButton
          title={I18n.t('confirm')}
          type="green"
          onClick={onPurchase}
          disabled={processing}
          scaleTitle
          className={style.confirmationButton}
        />
      )}
    </DialogWithTransition>
  );
};

ExchangeCurrencyDialog.propTypes = {
  lootId: string.isRequired,
  balance: number.isRequired,
  onPurchase: func.isRequired,
  processing: bool.isRequired,
  currency: string,
  price: oneOfType([number, string]),
  onClose: func,
  show: bool,
  bundleSelected: string,
};

export default ExchangeCurrencyDialog;
